import React, { useState, useMemo, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, Download, RefreshCw, ArrowLeft, Copy, Check } from 'lucide-react';
import {
  compareDecimals,
  isDecimalString,
  formatWithCommas,
} from '../services/Web3Services';
import { ethers } from 'ethers';

interface UserData {
  id: number;
  address: string;
  /** Why the user is or is not eligible right now. */
  status: string;
  isEligible: boolean;
  lastReceivingDateTime: string;
  amountReleased: string;
  /** Total tokens vested for this user (deposit equivalent). */
  vestedToken: string;
  nextReleaseDate: string;
  claimableAmount: string;
  startTime: string;
  lastClaimTime: string;
  tokenReleased: string;
}

interface AdminDashboardProps {
  setShowDashboard: (show: boolean) => void;
}

/**
 * Deterministic "blockies" identicon data (the pixel avatars Etherscan and
 * MetaMask use). The pattern and palette derive from the whole address, so
 * every row gets a visually distinct mark — unlike initials, where every
 * 0xE9… address looked identical.
 */
function makeBlockie(seed: string) {
  const randseed = new Int32Array(4);
  for (let i = 0; i < seed.length; i++) {
    randseed[i % 4] = (randseed[i % 4] << 5) - randseed[i % 4] + seed.charCodeAt(i);
  }
  const rand = () => {
    const t = randseed[0] ^ (randseed[0] << 11);
    randseed[0] = randseed[1];
    randseed[1] = randseed[2];
    randseed[2] = randseed[3];
    randseed[3] = randseed[3] ^ (randseed[3] >> 19) ^ t ^ (t >>> 8);
    return (randseed[3] >>> 0) / 2147483648;
  };
  const color = () => {
    const h = Math.floor(rand() * 360);
    const s = (rand() * 60 + 40).toFixed(1);
    const l = ((rand() + rand() + rand() + rand()) * 25).toFixed(1);
    return `hsl(${h},${s}%,${l}%)`;
  };
  const fg = color();
  const bg = color();
  const spot = color();
  const GRID = 8;
  // Each row is generated for the left half then mirrored — the symmetry is
  // what makes blockies read as "faces" rather than noise.
  const cells: number[] = [];
  for (let y = 0; y < GRID; y++) {
    const half: number[] = [];
    for (let x = 0; x < GRID / 2; x++) half.push(Math.floor(rand() * 2.3));
    cells.push(...half, ...half.slice().reverse());
  }
  return { cells, fg, bg, spot, grid: GRID };
}

const AddressIdenticon: React.FC<{ address: string; size?: number }> = React.memo(
  ({ address, size = 28 }) => {
    const { cells, fg, bg, spot, grid } = useMemo(
      () => makeBlockie(address.toLowerCase()),
      [address]
    );
    return (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${grid} ${grid}`}
        shapeRendering="crispEdges"
        className="flex-none rounded-md"
        aria-hidden="true"
      >
        <rect width={grid} height={grid} fill={bg} />
        {cells.map((v, i) =>
          v ? (
            <rect
              key={i}
              x={i % grid}
              y={Math.floor(i / grid)}
              width={1}
              height={1}
              fill={v === 1 ? fg : spot}
            />
          ) : null
        )}
      </svg>
    );
  }
);

/**
 * Identicon + middle-truncated address (full value on hover) + copy button.
 * Truncation frees the table's widest column; the copy button restores what
 * truncation takes away — you can no longer select the full text from the row.
 */
const AddressCell: React.FC<{ address: string }> = React.memo(({ address }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(address).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="flex items-center gap-2.5">
      <AddressIdenticon address={address} />
      <span className="text-[13px] text-[#D6E2DC] font-mono" title={address}>
        {address.slice(0, 8)}…{address.slice(-6)}
      </span>
      <button
        onClick={copy}
        title={copied ? 'Copied' : 'Copy full address'}
        aria-label="Copy full address"
        className="flex-none text-[#7A8880] hover:text-[#5EE9B5] transition-colors"
      >
        {copied ? <Check size={13} className="text-[#5EE9B5]" /> : <Copy size={13} />}
      </button>
    </div>
  );
}); 

// Contract configuration
const CONTRACT_ADDRESS = "0x4c66Ab8ECb605Ec21D7b7f0a9270b8Ff1aD71b81";
const CONTRACT_ABI = [
  {
    "inputs": [],
    "name": "depositorCount",
    "outputs": [{"name": "", "type": "uint256"}],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "lastScannedIndex",
    "outputs": [{"name": "", "type": "uint256"}],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [{"name": "index", "type": "uint256"}],
    "name": "getDepositor",
    "outputs": [{"name": "", "type": "address"}],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [{"name": "user", "type": "address"}],
    "name": "getUserDetails",
    "outputs": [
      {"name": "vestedToken", "type": "uint256"},
      {"name": "startTime", "type": "uint256"},
      {"name": "tokenReleased", "type": "uint256"},
      {"name": "claimableAmount", "type": "uint256"},
      {"name": "lastClaimTime", "type": "uint256"},
      {"name": "feesDeposited", "type": "bool"},
      {"name": "claimTriggered", "type": "bool"},
      {"name": "onHold", "type": "bool"},
      {"name": "adminApproved", "type": "bool"},
      {"name": "vestingStarted", "type": "bool"},
      {"name": "firstClaimDone", "type": "bool"}
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [{"name": "user", "type": "address"}],
    "name": "blockedUsers",
    "outputs": [{"name": "", "type": "bool"}],
    "stateMutability": "view",
    "type": "function"
  }
];

// Multicall3 contract address and ABI
const MULTICALL3_ADDRESS = "0xcA11bde05977b3631167028862bE2a173976CA11"; // BSC Multicall3
const MULTICALL3_ABI = [
  {
    "inputs": [
      {
        "components": [
          {"name": "target", "type": "address"},
          {"name": "allowFailure", "type": "bool"},
          {"name": "callData", "type": "bytes"}
        ],
        "name": "calls",
        "type": "tuple[]"
      }
    ],
    "name": "aggregate3",
    "outputs": [
      {
        "components": [
          {"name": "success", "type": "bool"},
          {"name": "returnData", "type": "bytes"}
        ],
        "name": "returnData",
        "type": "tuple[]"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  }
];

const AdminDashboard: React.FC<AdminDashboardProps> = ({ setShowDashboard }) => {
  const [searchTerm, setSearchTerm] = useState('');
  /** 'All' or one of the derived per-user statuses. */
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortField, setSortField] = useState('lastReceivingDateTime');
  const [sortDirection, setSortDirection] = useState('desc');
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
  
  // Summary stats
  const [totalDepositors, setTotalDepositors] = useState(0);
  const [totalEligible, setTotalEligible] = useState(0);
  const [totalClaimable, setTotalClaimable] = useState('0');
  /** Sum of tokenReleased across every user — how much has been paid out. */
  const [totalReleased, setTotalReleased] = useState('0');
  /**
   * Outstanding liability: vested minus released, summed over every user who
   * will eventually be paid (fees deposited, not blocked). Unlike claimable,
   * this includes tokens not yet accrued to the weekly schedule.
   */
  const [totalRemaining, setTotalRemaining] = useState('0');
  /** Non-fatal: set when the scan could not load every depositor. */
  const [scanWarning, setScanWarning] = useState<string | null>(null);

  // Initialize contracts
  const [contract, setContract] = useState<ethers.Contract | null>(null);
  const [multicall, setMulticall] = useState<ethers.Contract | null>(null);
  // Round-robin pool: batches are spread across endpoints so they run truly in
  // parallel instead of queueing behind one rate-limited node.
  const [multicallPool, setMulticallPool] = useState<ethers.Contract[]>([]);
  const [provider, setProvider] = useState<ethers.Provider | null>(null);
  const [contractInterface, setContractInterface] = useState<ethers.Interface | null>(null);

  useEffect(() => {
    const initContract = async () => {
      try {
        // Build a POOL of providers rather than picking one. A single BSC node
        // rate-limits concurrent multicalls, so batches fired in parallel at one
        // endpoint queue behind each other. Spreading them round-robin across
        // several endpoints cut a 530-depositor scan from ~14.6s to ~3.8s.
        // (The old bsc-dataseed*.binance.org hosts are dead; these are current.)
        const rpcUrls = [
          'https://bsc-dataseed.bnbchain.org/',
          'https://bsc-dataseed1.defibit.io/',
          'https://bsc-rpc.publicnode.com',
          'https://bsc-dataseed.bnbchain.org/',
        ];

        const live: ethers.JsonRpcProvider[] = [];
        await Promise.all(
          rpcUrls.map(async (url) => {
            try {
              const prov = new ethers.JsonRpcProvider(url);
              await prov.getNetwork();
              live.push(prov);
            } catch {
              console.warn(`[AdminDashboard] RPC unavailable: ${url}`);
            }
          })
        );

        if (!live.length) {
          throw new Error('All RPC endpoints failed');
        }

        setProvider(live[0]);
        setMulticallPool(
          live.map((prov) => new ethers.Contract(MULTICALL3_ADDRESS, MULTICALL3_ABI, prov))
        );

        // Initialize contracts
        const vestingContract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, live[0]);
        setContract(vestingContract);

        const multicallContract = new ethers.Contract(MULTICALL3_ADDRESS, MULTICALL3_ABI, live[0]);
        setMulticall(multicallContract);

        // Create interface for encoding/decoding
        const iface = new ethers.Interface(CONTRACT_ABI);
        setContractInterface(iface);

      } catch (err) {
        console.error('Failed to initialize contract:', err);
        setError('Failed to connect to BSC network');
      }
    };

    initContract();
  }, []);

  // Batch fetch user addresses
const batchFetchAddresses = async (
  startIndex: number,
  endIndex: number,
  mc?: ethers.Contract
) => {
  const client = mc ?? multicall;
  if (!client || !contractInterface) return [];

  const calls = [];
  for (let i = startIndex; i <= endIndex; i++) {
    const callData = contractInterface.encodeFunctionData('getDepositor', [i]);
    calls.push({
      target: CONTRACT_ADDRESS,
      allowFailure: true,
      callData: callData
    });
  }
  
  // Use staticCall for read-only operation
  const results = await client.aggregate3.staticCall(calls);
  const addresses = [];
  
  for (let i = 0; i < results.length; i++) {
    if (results[i].success && results[i].returnData !== '0x') {
      try {
        const decoded = contractInterface.decodeFunctionResult('getDepositor', results[i].returnData);
        addresses.push({ index: startIndex + i, address: decoded[0] });
      } catch (err) {
        console.error(`Failed to decode address at index ${startIndex + i}:`, err);
      }
    }
  }
  
  return addresses;
};

  // Batch fetch user details
const batchFetchUserDetails = async (
  addresses: { index: number, address: string }[],
  mc?: ethers.Contract
) => {
  const client = mc ?? multicall;
  if (!client || !contractInterface) return [];

  const calls = [];
  
  // Prepare calls for getUserDetails and blockedUsers
  for (const { address } of addresses) {
    // getUserDetails call
    calls.push({
      target: CONTRACT_ADDRESS,
      allowFailure: true,
      callData: contractInterface.encodeFunctionData('getUserDetails', [address])
    });
    
    // blockedUsers call
    calls.push({
      target: CONTRACT_ADDRESS,
      allowFailure: true,
      callData: contractInterface.encodeFunctionData('blockedUsers', [address])
    });
  }
  
  // Use staticCall for read-only operation
  const results = await client.aggregate3.staticCall(calls);
  const userDataList = [];
  
  for (let i = 0; i < addresses.length; i++) {
    const detailsIndex = i * 2;
    const blockedIndex = i * 2 + 1;
    
    if (results[detailsIndex].success && results[blockedIndex].success) {
      try {
        const details = contractInterface.decodeFunctionResult('getUserDetails', results[detailsIndex].returnData);
        const isBlocked = contractInterface.decodeFunctionResult('blockedUsers', results[blockedIndex].returnData)[0];
        
        userDataList.push({
          address: addresses[i].address,
          details: details,
          isBlocked: isBlocked
        });
      } catch (err) {
        console.error(`Failed to decode data for address ${addresses[i].address}:`, err);
      }
    }
  }
  
  return userDataList;
};

  // Optimized fetch function using multicall
  const fetchUsersFromBlockchainOptimized = async () => {
    if (!contract || !multicall || !contractInterface || !provider) {
      setError('Contracts not initialized');
      return;
    }

    setLoading(true);
    setError(null);
    setLoadingProgress({ current: 0, total: 0 });

    try {
      // Get depositor count
      const depositorCount = await contract.depositorCount();
      const depositorCountNumber = Number(depositorCount);
      setTotalDepositors(depositorCountNumber);
      setLoadingProgress({ current: 0, total: depositorCountNumber });

      console.log(`Starting optimized scan of ${depositorCountNumber} users...`);

      const allUsers: UserData[] = [];
      let eligibleCount = 0;
      let totalClaimableBigInt = 0n;
      let totalReleasedBigInt = 0n;
      let totalRemainingBigInt = 0n;

      // Process in batches
      const BATCH_SIZE = 50; // Adjust based on RPC limits
      const batches = Math.ceil(depositorCountNumber / BATCH_SIZE);

      // Run every batch concurrently, each pinned to a different RPC endpoint
      // round-robin. Sequentially this took ~14.6s for 530 depositors because
      // each batch waited on the previous one; in parallel across the pool it
      // is ~3.8s. Batches are independent, so ordering does not matter — the
      // results are re-sorted by index afterwards for a stable list.
      let done = 0;
      const pool = multicallPool.length ? multicallPool : (multicall ? [multicall] : []);
      const batchResults = await Promise.all(
        Array.from({ length: batches }, async (_, batch) => {
          const startIndex = batch * BATCH_SIZE;
          const endIndex = Math.min(startIndex + BATCH_SIZE - 1, depositorCountNumber - 1);
          const expected = endIndex - startIndex + 1;
          // Retry a short batch against the OTHER endpoints before accepting
          // it: public BSC nodes rate-limit bursts, and a dropped batch used
          // to vanish silently (532 depositors would render as ~463 rows,
          // with the strip totals computed from the partial set).
          let best: Awaited<ReturnType<typeof batchFetchUserDetails>> = [];
          for (let attempt = 0; attempt < pool.length; attempt++) {
            const mc = pool[(batch + attempt) % pool.length];
            try {
              const addresses = await batchFetchAddresses(startIndex, endIndex, mc);
              const userDataBatch = await batchFetchUserDetails(addresses, mc);
              if (userDataBatch.length > best.length) best = userDataBatch;
              if (best.length >= expected) break;
              console.warn(`Batch ${batch}: got ${userDataBatch.length}/${expected}, retrying on next RPC`);
            } catch (err) {
              console.error(`Error processing batch ${batch} (attempt ${attempt + 1}):`, err);
            }
            // Brief backoff so the retry doesn't hit the same rate limiter.
            await new Promise((r) => setTimeout(r, 250 * (attempt + 1)));
          }
          done += expected;
          setLoadingProgress({ current: Math.min(done, depositorCountNumber), total: depositorCountNumber });
          return best; // possibly partial — reported below if so
        })
      );

      {
        // Flatten in batch order so the resulting list is deterministic.
        const allUserData = batchResults.flat();
        for (const userData of allUserData) {
            const [
              vestedToken,
              startTime,
              tokenReleased,
              claimableAmount,
              lastClaimTime,
              feesDeposited,
              claimTriggered,
              onHold,
              adminApproved,
              vestingStarted,
              firstClaimDone
            ] = userData.details;
            
            // Eligibility, and — when not eligible — the specific reason, in
            // the same priority order the contract effectively applies.
            const isEligible =
              feesDeposited &&
              !claimTriggered &&
              vestingStarted &&
              !userData.isBlocked &&
              (!onHold || adminApproved) &&
              claimableAmount > 0n;

            let status = 'Eligible';
            if (userData.isBlocked) status = 'Blocked';
            else if (claimTriggered) status = 'Claimed';
            else if (!feesDeposited) status = 'Fees pending';
            else if (!vestingStarted) status = 'Not started';
            else if (onHold && !adminApproved) status = 'On hold';
            else if (claimableAmount === 0n) status = 'Nothing due';

            totalReleasedBigInt += tokenReleased;
            // Outstanding = what this user is still owed over the whole
            // schedule. Count anyone who will eventually be paid: fees in,
            // not blocked — regardless of whether anything is claimable today.
            if (feesDeposited && !userData.isBlocked && vestedToken > tokenReleased) {
              totalRemainingBigInt += BigInt(vestedToken) - BigInt(tokenReleased);
            }
            if (isEligible) {
              eligibleCount++;
              totalClaimableBigInt += claimableAmount;
            }

            // Zero timestamps (never started / never claimed) must not render
            // as 1970 — pass '' so formatDate shows N/A instead.
            const startTs = Number(startTime);
            const lastTs = Number(lastClaimTime);
            const startIso = startTs > 0 ? new Date(startTs * 1000).toISOString() : '';
            const lastIso = lastTs > 0 ? new Date(lastTs * 1000).toISOString() : '';
            // Next release: the schedule is a fixed weekly cadence anchored at
            // vesting start (the fee-deposit date). Do NOT anchor on the last
            // claim — the bot sometimes catches up in bulk on a random later
            // date (gas shortages), which would drift every subsequent date.
            const WEEK = 7 * 24 * 60 * 60;
            let nextIso = '';
            if (startTs > 0) {
              const nowSec = Math.floor(Date.now() / 1000);
              const elapsed = Math.max(0, nowSec - startTs);
              const nextTick = startTs + (Math.floor(elapsed / WEEK) + 1) * WEEK;
              nextIso = new Date(nextTick * 1000).toISOString();
            }

            allUsers.push({
              id: allUsers.length + 1,
              address: userData.address,
              status,
              isEligible,
              lastReceivingDateTime: lastIso,
              amountReleased: ethers.formatEther(tokenReleased),
              vestedToken: ethers.formatEther(vestedToken),
              nextReleaseDate: nextIso,
              claimableAmount: ethers.formatEther(claimableAmount),
              startTime: startIso,
              lastClaimTime: lastIso,
              tokenReleased: ethers.formatEther(tokenReleased)
            });
        }
      }

      // Update state
      setUsers(allUsers);
      setTotalEligible(eligibleCount);
      setTotalClaimable(ethers.formatEther(totalClaimableBigInt));
      setTotalReleased(ethers.formatEther(totalReleasedBigInt));
      setTotalRemaining(ethers.formatEther(totalRemainingBigInt));
      
      console.log(`Scan complete: ${allUsers.length} users, ${eligibleCount} eligible`);
      console.log(`Total Claimable: ${ethers.formatEther(totalClaimableBigInt)} tokens`);

      // If retries still could not recover every row, say so instead of
      // presenting a partial table (and partial totals) as complete.
      setScanWarning(
        allUsers.length < depositorCountNumber
          ? `Loaded ${allUsers.length} of ${depositorCountNumber} depositors — some RPC batches failed even after retries, so the figures above cover loaded users only. Refresh to retry.`
          : null
      );

    } catch (err: any) {
      console.error('Error fetching blockchain data:', err);
      setError(err.message || 'Failed to fetch data from blockchain');
    } finally {
      setLoading(false);
      setLoadingProgress({ current: 0, total: 0 });
    }
  };

  // Initial fetch
  useEffect(() => {
    // Wait for the pool too, otherwise the first scan runs single-endpoint.
    if (contract && multicall && contractInterface && provider && multicallPool.length) {
      fetchUsersFromBlockchainOptimized();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract, multicall, contractInterface, provider, multicallPool]);

  // Filter and search users
  const filteredUsers = useMemo(() => {
    let filtered = users.filter(user =>
      user.address.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (statusFilter === 'All' || user.status === statusFilter)
    );

    // Sort users
    filtered.sort((a, b) => {
      const aValue = a[sortField as keyof UserData];
      const bValue = b[sortField as keyof UserData];
      const direction = sortDirection === 'asc' ? 1 : -1;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        // For date strings, compare as dates
        if (sortField.includes('Date') || sortField.includes('Time')) {
          return (new Date(aValue).getTime() - new Date(bValue).getTime()) * direction;
        }
        // For numeric strings (amounts), compare exactly. parseFloat would
        // collapse 18-decimal values that differ only beyond ~15 digits,
        // making distinct balances sort as equal.
        if (isDecimalString(aValue) && isDecimalString(bValue)) {
          return compareDecimals(aValue, bValue) * direction;
        }
        // Default string comparison
        return aValue.localeCompare(bValue) * direction;
      }
      return 0;
    });

    return filtered;
  }, [users, searchTerm, statusFilter, sortField, sortDirection]);

  /** How many users hold each status, for the filter pills. */
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    users.forEach(u => { counts[u.status] = (counts[u.status] || 0) + 1; });
    return counts;
  }, [users]);

  // Pagination
  const paginatedUsers = useMemo(() => {
    if (itemsPerPage === -1) { // -1 represents "All"
      return filteredUsers;
    }
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredUsers, currentPage, itemsPerPage]);

  const totalPages = itemsPerPage === -1 ? 1 : Math.ceil(filteredUsers.length / itemsPerPage);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateHash = (hash: string) => {
    if (!hash || hash === '0x' + '0'.repeat(64)) return 'N/A';
    return `${hash.slice(0, 6)}...${hash.slice(-4)}`;
  };


  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <ChevronDown className="w-4 h-4 opacity-50" />;
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-4 h-4 text-blue-400" /> : 
      <ChevronDown className="w-4 h-4 text-blue-400" />;
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Address', 'Status', 'Total Vested', 'Amount Released', 'Claimable', 'Last Receiving', 'Next Release'];
    const rows = filteredUsers.map(user => [
      user.address,
      user.status,
      user.vestedToken,
      user.amountReleased,
      user.claimableAmount,
      formatDate(user.lastReceivingDateTime),
      formatDate(user.nextReleaseDate)
    ]);

    // Dates from formatDate contain commas ("Aug 10, 2026, 08:30 PM"), which
    // silently shifted columns in the exported file — quote every field.
    const q = (v: string) => '"' + String(v).replace(/"/g, '""') + '"';
    const csvContent = [
      headers.map(q).join(','),
      ...rows.map(row => row.map(q).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vesting_users_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen text-white font-sora p-6" style={{ background: `radial-gradient(1200px 700px at top left, rgb(2, 36, 19), rgb(0, 2, 5) 30%)` }}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#5EE9B5]">CVT V1 &rarr; V2 Vesting</div>
            <h1 className="text-3xl font-bold text-white mt-1.5">User dashboard</h1>
            <p className="text-[13px] text-[#8A9992] mt-1">Vesting status for every depositor, read from BSC</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowDashboard(false)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#5EE9B540] bg-[#5EE9B514] text-[#5EE9B5] text-xs font-semibold hover:bg-[#5EE9B524] transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
            <button
              onClick={fetchUsersFromBlockchainOptimized}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#5EE9B540] bg-[#5EE9B514] text-[#5EE9B5] text-xs font-semibold hover:bg-[#5EE9B524] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Loading…' : 'Refresh'}
            </button>
            <button
              onClick={exportToCSV}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#5EE9B540] bg-[#5EE9B514] text-[#5EE9B5] text-xs font-semibold hover:bg-[#5EE9B524] transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-3 rounded-lg">
            <span>Error: {error}</span>
          </div>
        )}

        {/* Loading Progress */}
        {loading && loadingProgress.total > 0 && (
          <div className="mb-6 bg-[#5EE9B510] border border-[#5EE9B530] rounded-lg p-4">
            <div className="flex justify-between text-sm text-[#5EE9B5] mb-2">
              <span>Processing users...</span>
              <span>{loadingProgress.current} / {loadingProgress.total}</span>
            </div>
            <div className="w-full bg-[#5EE9B520] rounded-full h-2">
              <div 
                className="bg-[#34D399] h-2 rounded-full transition-all duration-300"
                style={{ width: `${(loadingProgress.current / loadingProgress.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Search + status filter share one row; search stays compact. */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#7A8880]" />
            <input
              type="text"
              placeholder="Search address..."
              className="w-full pl-9 pr-3 py-1.5 text-sm bg-black/40 text-white placeholder-[#5A665F] border border-[#5EE9B5]/30 rounded-full hover:border-[#5EE9B5]/50 focus:outline-none focus:ring-2 focus:ring-[#5EE9B5]/40 focus:border-transparent transition-colors"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {['All', 'Eligible', 'Blocked', 'Claimed', 'Fees pending', 'Not started', 'On hold', 'Nothing due']
              .filter((st) => st === 'All' || statusCounts[st])
              .map((st) => {
                const on = statusFilter === st;
                const count = st === 'All' ? users.length : statusCounts[st] || 0;
                return (
                  <button
                    key={st}
                    onClick={() => { setStatusFilter(st); setCurrentPage(1); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      on
                        ? 'border-[#5EE9B5]/70 bg-[#5EE9B5]/25 text-[#5EE9B5]'
                        : 'border-[#5EE9B5]/25 bg-[#5EE9B5]/[0.06] text-[#D6E2DC] hover:border-[#5EE9B5]/50 hover:bg-[#5EE9B5]/15 hover:text-[#5EE9B5]'
                    }`}
                  >
                    {st} <span className="opacity-60">{count}</span>
                  </button>
                );
              })}
          </div>
        </div>

        {scanWarning && (
          <div className="flex items-start gap-2 rounded-lg border border-[rgba(245,180,76,0.32)] bg-[rgba(245,180,76,0.10)] px-4 py-3 mb-4 text-[13px] text-[#F5B44C]">
            <span className="font-semibold flex-none">Partial data:</span>
            <span className="text-[#D6E2DC]">{scanWarning}</span>
          </div>
        )}

        {/* Summary strip — compact cells like the admin console's status
            strip. Values stay white for readability; the colour accent lives
            in a small dot beside the label instead of tinting the number. */}
        <div className="flex flex-wrap gap-px bg-white/[0.07] border border-white/[0.07] rounded-xl overflow-hidden mb-6">
          <div className="flex-1 min-w-[150px] bg-[#0A1310] px-4 py-3">
            <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#7A8880]">Total Depositors</p>
            {/* Two-tier hierarchy: only the actionable figures (Eligible,
                Claimable Now) are white/large; context figures are muted and
                a step smaller so the strip has one focal point, not five. */}
            <p className="text-base font-medium text-[#8A9992] mt-1">{totalDepositors}</p>
          </div>
          <div className="flex-1 min-w-[150px] bg-[#0A1310] px-4 py-3">
            <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#7A8880]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#5EE9B5]" />Eligible Users
            </p>
            <p className="text-lg font-semibold text-white mt-1">{totalEligible}</p>
          </div>
          <div className="flex-1 min-w-[150px] bg-[#0A1310] px-4 py-3">
            <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#7A8880]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#34D399]" />Total Released
            </p>
            <p className="text-base font-medium text-[#8A9992] mt-1" title={`${totalReleased} tokens`}>
              {formatWithCommas(totalReleased, 2)} <span className="text-xs text-[#7A8880]">tokens</span>
            </p>
          </div>
          <div className="flex-1 min-w-[150px] bg-[#0A1310] px-4 py-3">
            <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#7A8880]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#60A5FA] dot-breathe" />Claimable Now
            </p>
            <p className="text-lg font-semibold text-white mt-1" title={`${totalClaimable} tokens`}>
              {formatWithCommas(totalClaimable, 2)} <span className="text-xs text-[#8A9992]">tokens</span>
            </p>
          </div>
          <div className="flex-1 min-w-[150px] bg-[#0A1310] px-4 py-3">
            <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#7A8880]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E08A5C]" />Left to Release
            </p>
            <p className="text-base font-medium text-[#8A9992] mt-1" title={`${totalRemaining} tokens total still owed to payable users (fees paid, not blocked) over the full schedule`}>
              {formatWithCommas(totalRemaining, 2)} <span className="text-xs text-[#7A8880]">tokens</span>
            </p>
          </div>
        </div>
      </div>

      {/* Main Data Table */}
      <div className="bg-[linear-gradient(160deg,rgba(52,211,153,0.07),rgba(255,255,255,0.025)_55%)] border border-[rgba(94,233,181,0.16)] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0A1310] border-b border-[rgba(94,233,181,0.16)]">
              <tr>
                <th className="px-6 py-4 text-left">
                  <button 
                    onClick={() => handleSort('address')}
                    className="flex items-center space-x-1 text-xs font-medium text-gray-300 uppercase tracking-wider hover:text-[#5EE9B5]"
                  >
                    <span>Address</span>
                    <SortIcon field="address" />
                  </button>
                </th>
                <th className="px-6 py-4 text-left">
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center space-x-1 text-xs font-medium text-gray-300 uppercase tracking-wider hover:text-[#5EE9B5]"
                  >
                    <span>Status</span>
                    <SortIcon field="status" />
                  </button>
                </th>
                <th className="px-6 py-4 text-left">
                  <button
                    onClick={() => handleSort('vestedToken')}
                    className="flex items-center space-x-1 text-xs font-medium text-gray-300 uppercase tracking-wider hover:text-[#5EE9B5]"
                  >
                    <span>Total Vested</span>
                    <SortIcon field="vestedToken" />
                  </button>
                </th>
                <th className="px-6 py-4 text-left">
                  <button 
                    onClick={() => handleSort('lastReceivingDateTime')}
                    className="flex items-center space-x-1 text-xs font-medium text-gray-300 uppercase tracking-wider hover:text-[#5EE9B5]"
                  >
                    <span>Last Receiving</span>
                    <SortIcon field="lastReceivingDateTime" />
                  </button>
                </th>
                <th className="px-6 py-4 text-left">
                  <button 
                    onClick={() => handleSort('amountReleased')}
                    className="flex items-center space-x-1 text-xs font-medium text-gray-300 uppercase tracking-wider hover:text-[#5EE9B5]"
                  >
                    <span>Amount Released</span>
                    <SortIcon field="amountReleased" />
                  </button>
                </th>
                <th className="px-6 py-4 text-left">
                  <button 
                    onClick={() => handleSort('nextReleaseDate')}
                    className="flex items-center space-x-1 text-xs font-medium text-gray-300 uppercase tracking-wider hover:text-[#5EE9B5]"
                  >
                    <span>Next Release</span>
                    <SortIcon field="nextReleaseDate" />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.06]">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#5EE9B5] mb-2" />
                    <p className="text-gray-400">
                      Loading users from blockchain...
                      {loadingProgress.total > 0 && (
                        <span className="block mt-2 text-sm">
                          {loadingProgress.current} / {loadingProgress.total} users processed
                        </span>
                      )}
                    </p>
                  </td>
                </tr>
              ) : paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <p className="text-gray-400 text-lg mb-2">No users found</p>
                    <p className="text-gray-400">Try adjusting the search or the status filter</p>
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-white/[0.03] transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <AddressCell address={user.address} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                          user.isEligible
                            ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                            : user.status === 'Blocked'
                            ? 'bg-red-500/15 text-red-400 border border-red-500/30'
                            : user.status === 'Claimed'
                            ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30'
                            : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/25'
                        }`}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-white" title={`${user.vestedToken} tokens`}>
                        {formatWithCommas(user.vestedToken, 2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-[#8A9992]">{formatDate(user.lastReceivingDateTime)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-[#D6E2DC]">{formatWithCommas(user.amountReleased, 2)}</div>
                      <div className={`text-xs ${user.isEligible ? 'text-[#5EE9B5]' : 'text-[#7A8880]'}`}>
                        Claimable: {formatWithCommas(user.claimableAmount, 2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-[#8A9992]">
                        {/* Nothing more is coming to claimed-out or blocked
                            users — a date here would be misleading. */}
                        {user.status === 'Claimed' || user.status === 'Blocked'
                          ? '—'
                          : formatDate(user.nextReleaseDate)}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {filteredUsers.length > 0 && (
          <div className="px-6 py-4 bg-[#0A1310] border-t border-[rgba(94,233,181,0.16)]">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-[#8A9992]">Rows per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-2 py-1 bg-black/40 text-[#D6E2DC] border border-white/[0.12] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#5EE9B5]/40"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={-1}>All</option>
                </select>
              </div>

              {itemsPerPage !== -1 && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm rounded-full border border-[#5EE9B5]/25 bg-[#5EE9B5]/[0.06] text-[#D6E2DC] hover:border-[#5EE9B5]/50 hover:bg-[#5EE9B5]/15 hover:text-[#5EE9B5] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[#5EE9B5]/25 disabled:hover:bg-[#5EE9B5]/[0.06] disabled:hover:text-[#D6E2DC]"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-[#8A9992]">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm rounded-full border border-[#5EE9B5]/25 bg-[#5EE9B5]/[0.06] text-[#D6E2DC] hover:border-[#5EE9B5]/50 hover:bg-[#5EE9B5]/15 hover:text-[#5EE9B5] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[#5EE9B5]/25 disabled:hover:bg-[#5EE9B5]/[0.06] disabled:hover:text-[#D6E2DC]"
                  >
                    Next
                  </button>
                </div>
              )}

              <div className="text-sm text-[#8A9992]">
                Showing {paginatedUsers.length} of {filteredUsers.length} results
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;